import React, { useCallback } from 'react';
import { postSongCollection, SongCollection, uploadSongCollectionImage } from '@round/api';
import Modal, { ModalContent, ModalTitle } from '../../../../ui/Layout/Modals/Modal/Modal';
import SongCollectionForm, { SongCollectionFormProps } from '../../components/SongCollectionForm/SongCollectionForm';
import { showNotification } from '../../../../helpers';

type CreateSongCollectionModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onCreated: (collection: SongCollection) => void;
};

const CreateSongCollectionModal = ({ onClose, isOpen, onCreated }: CreateSongCollectionModalProps) => {
    const handleSubmit: SongCollectionFormProps['handleSubmit'] = useCallback(
        async (values, { setSubmitting }) => {
            if (!values.client) {
                return;
            }

            try {
                setSubmitting(true);
                const { data: collection } = await postSongCollection({
                    name: values.name,
                    client: values.client.value,
                });

                const [image] = values.image;
                if (!image) {
                    onCreated(collection);
                    showNotification('Song collection created', 'info');
                    return;
                }

                const imageResponse = await uploadSongCollectionImage(collection.id, image);
                if (imageResponse.status === 404) {
                    showNotification(imageResponse.data.detail, 'error');
                    return;
                }

                onCreated(imageResponse.data);
                showNotification('Song collection created', 'info');
            } catch {
                showNotification('Could not create song collection', 'error');
            } finally {
                setSubmitting(false);
            }
        },
        [onCreated]
    );

    return (
        <Modal closeOnOverlayClick isOpen={isOpen} onClose={onClose}>
            <ModalTitle>Create Song Collection</ModalTitle>
            <ModalContent>
                <SongCollectionForm handleSubmit={handleSubmit} />
            </ModalContent>
        </Modal>
    );
};

export default CreateSongCollectionModal;
